import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { FlowStatus, FlowSummary } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { listFlows } from "@/lib/flows-api";

// TES-165 — clinician/coach flow surface. Lists every scheduled content
// journey (flow) with its trigger, publish status and version. The day-grid
// builder lives at /flows/:key.

const STATUS_LABELS: Record<FlowStatus, string> = {
  draft: "Utkast",
  published: "Publicerad",
  archived: "Arkiverad",
};

const TRIGGER_EVENT_LABELS: Record<string, string> = {
  first_consult_completed: "Efter första besöket",
};

function statusChip(status: FlowStatus) {
  const cls =
    status === "published"
      ? "bg-success/10 text-success border-success/30"
      : status === "archived"
        ? "bg-surfaceMuted text-textSecondary border-surfaceMuted"
        : "bg-warning/10 text-warning border-warning/30";
  return (
    <span
      className={`inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border ${cls}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function triggerLabel(row: FlowSummary): string {
  if (row.triggerType === "manual") return "Manuell";
  return row.triggerEvent
    ? (TRIGGER_EVENT_LABELS[row.triggerEvent] ?? row.triggerEvent)
    : "Händelse";
}

export function FlowsListRoute() {
  const { signOut } = useAuth();
  const [rows, setRows] = useState<FlowSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listFlows()
      .then(setRows)
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  }, [signOut]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">Flöden</h1>
        <Link
          to="/flows/new"
          className="rounded-md bg-brand text-white px-md py-sm text-sm font-medium hover:bg-brand/90 transition"
        >
          Nytt flöde
        </Link>
      </div>

      <p className="text-sm text-textSecondary max-w-2xl">
        Schemalagda innehållsresor: en sekvens av hemkort och påminnelser
        (push/e-post) utspridda över dagar, som tilldelas en patient manuellt
        eller av en händelse. Ändringar sparas som utkast och börjar gälla först
        när du publicerar.
      </p>

      <div className="flex items-end">
        <span className="text-sm text-textSecondary ml-auto">
          {loading ? "Hämtar…" : rows ? `${rows.length} flöden` : ""}
        </span>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {rows && rows.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          Inga flöden ännu. Skapa ditt första med “Nytt flöde”.
        </div>
      ) : null}

      {rows && rows.length > 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surfaceMuted/60 text-textSecondary uppercase tracking-wide text-xs">
              <tr>
                <th className="text-left px-md py-sm font-medium">Namn</th>
                <th className="text-left px-md py-sm font-medium">Nyckel</th>
                <th className="text-left px-md py-sm font-medium">Trigger</th>
                <th className="text-left px-md py-sm font-medium">Status</th>
                <th className="text-left px-md py-sm font-medium">Version</th>
                <th className="text-left px-md py-sm font-medium">Steg</th>
                <th className="px-md py-sm" aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  className="border-t border-surfaceMuted hover:bg-surfaceMuted/40 transition"
                >
                  <td className="px-md py-sm font-medium">
                    {row.name || "(utan namn)"}
                    {/* TES-363 — surface TES-284 packages in the list so a
                        reviewer can tell a gated package from a plain flow. */}
                    {row.kind === "package" ? (
                      <span className="ml-sm inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border bg-brand/10 text-brand border-brand/30 align-middle">
                        Paket
                      </span>
                    ) : null}
                    {row.testOnly ? (
                      <span className="ml-sm inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border bg-warning/15 text-warning border-warning/30 align-middle">
                        TEST_ONLY
                      </span>
                    ) : null}
                  </td>
                  <td className="px-md py-sm font-mono text-xs text-textSecondary">
                    {row.key}
                  </td>
                  <td className="px-md py-sm text-textSecondary">
                    {triggerLabel(row)}
                  </td>
                  <td className="px-md py-sm">{statusChip(row.status)}</td>
                  <td className="px-md py-sm text-textSecondary">
                    v{row.version}
                  </td>
                  <td className="px-md py-sm text-textSecondary">
                    {row.itemCount}
                  </td>
                  <td className="px-md py-sm text-right">
                    <Link
                      to={`/flows/${encodeURIComponent(row.key)}`}
                      className="text-brand hover:underline text-sm font-medium"
                    >
                      Redigera →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
